// Crownline Crossing (plan §26). Mirror symmetry across x = 120.

import type { MapDef, MapId, MatchMode, PlayerId, RuinSpec } from './types';

const W = 240;
const H = 160;
const MX = 120; // mirror axis

const mirrorX = (x: number) => W - x;

// ---------------------------------------------------------------- base rubble ring
// A three-sided enclosure of walkable rubble around each core territory (the map edge is the
// fourth wall), enclosing the base deposits. Built out, the full castle costs ~200 Power
// (15 segments × 13 // TEST) with one traffic gate in the east wall facing the lanes.
// Wall footprint is 2×12: rot 0 = north–south segment, rot 1 = east–west segment.
const RING_SEGMENT_COST = 13; // TEST — ~200 Power for the full ring
const RING_NORTH_Y = 110;
const RING_SOUTH_Y = 50;
const RING_EAST_X = 54;

function baseRing(mirror: boolean): RuinSpec[] {
  const mx = (x: number) => (mirror ? mirrorX(x) : x);
  const group = mirror ? 'base-e' : 'base-w'; // one bulk build per base (owner request)
  const segs: RuinSpec[] = [];
  // north + south walls (east–west segments from the map edge toward the lane)
  for (const y of [RING_NORTH_Y, RING_SOUTH_Y]) {
    for (const x of [6, 18, 30, 42, 50]) {
      segs.push({ pos: { x: mx(x), y }, cost: RING_SEGMENT_COST, gateSpan: 0, rot: 1, group });
    }
  }
  // east wall (north–south segments); the center segment carries the main gate out to the lanes
  for (const y of [56, 68, 80, 92, 104]) {
    segs.push({
      pos: { x: mx(RING_EAST_X), y },
      cost: RING_SEGMENT_COST,
      gateSpan: y === 80 ? 8 : 0,
      rot: 0,
      group,
    });
  }
  return segs;
}

export const CROWNLINE_CROSSING: MapDef = {
  id: 'crownline-crossing',
  name: 'Crownline Crossing',
  description: 'The classic five-site battlefield: a direct center clash with broad northern and southern flanks.',
  mode: '1v1',
  width: W,
  height: H,
  corePositions: [
    { x: 24, y: 80 },
    { x: 216, y: 80 },
  ],
  spawnTeams: [0, 1],
  coreTerritoryRadius: 28,
  sites: [
    { id: 'site-wf', name: 'West Flank', pos: { x: 72, y: 80 } },
    { id: 'site-ef', name: 'East Flank', pos: { x: 168, y: 80 } },
    { id: 'site-c', name: 'Center', pos: { x: MX, y: 80 } },
    { id: 'site-n', name: 'North', pos: { x: MX, y: 130 } },
    { id: 'site-s', name: 'South', pos: { x: MX, y: 30 } },
  ],
  deposits: [
    // West core territory (A-13: 2× 2,000 Power + 600 Essence)
    { type: 'power', pos: { x: 14, y: 68 }, amount: 2000, gatherSlots: 4 },
    { type: 'power', pos: { x: 14, y: 92 }, amount: 2000, gatherSlots: 4 },
    { type: 'essence', pos: { x: 32, y: 62 }, amount: 600, gatherSlots: 4 },
    // East core territory (mirrored)
    { type: 'power', pos: { x: mirrorX(14), y: 68 }, amount: 2000, gatherSlots: 4 },
    { type: 'power', pos: { x: mirrorX(14), y: 92 }, amount: 2000, gatherSlots: 4 },
    { type: 'essence', pos: { x: mirrorX(32), y: 62 }, amount: 600, gatherSlots: 4 },
    // Flank expansions (2,500 Power each)
    { type: 'power', pos: { x: 64, y: 92 }, amount: 2500, gatherSlots: 4 },
    { type: 'power', pos: { x: mirrorX(64), y: 92 }, amount: 2500, gatherSlots: 4 },
    // North/South Essence (1,200 each)
    { type: 'essence', pos: { x: MX, y: 141 }, amount: 1200, gatherSlots: 4 },
    { type: 'essence', pos: { x: MX, y: 19 }, amount: 1200, gatherSlots: 4 },
    // Center rich node (3,000 P + 1,500 E)
    { type: 'power', pos: { x: MX, y: 92 }, amount: 3000, gatherSlots: 4 },
    { type: 'essence', pos: { x: MX, y: 68 }, amount: 1500, gatherSlots: 4 },
  ],
  // choke ruins seal the center lane (default cost/gate); ring rubble encircles each base
  ruins: [
    // center-lane choke walls: a giant-safe traffic gate, mountable battlements (2 slots)
    { pos: { x: 97, y: 80 }, gateSpan: 8 },
    { pos: { x: 143, y: 80 }, gateSpan: 8 },
    ...baseRing(false),
    ...baseRing(true),
  ],
  // rocks block the ellipse inscribed in each box (nav.ts) — sizes tuned down from the
  // original 10x38 ridges after owner feedback: units wedged on the choke corners
  rocks: [
    // Center-lane chokes: ridge pairs whose rounded ends meet the choke wall (y 74–86), so a
    // raised wall fully seals the lane except its 8 m shared auto-gate (owner: walls here were
    // walkaroundable). Corner-wedging is handled by nav clearance inflation, not smaller rocks.
    { pos: { x: 97, y: 55 }, w: 7, h: 38 }, // covers y 36–74, meets wall top // TEST
    { pos: { x: 97, y: 105 }, w: 7, h: 38 }, // covers y 86–124, meets wall bottom // TEST
    { pos: { x: 143, y: 55 }, w: 7, h: 38 }, // TEST
    { pos: { x: 143, y: 105 }, w: 7, h: 38 }, // TEST
    // Corner cover near N/S lanes
    { pos: { x: 60, y: 142 }, w: 12, h: 8 }, // TEST
    { pos: { x: 180, y: 142 }, w: 12, h: 8 }, // TEST
    { pos: { x: 60, y: 18 }, w: 12, h: 8 }, // TEST
    { pos: { x: 180, y: 18 }, w: 12, h: 8 }, // TEST
  ],
};

// ---------------------------------------------------------------- additional battlefields

function mirroredSites(width: number, entries: { id: string; name: string; x: number; y: number }[]) {
  return entries.flatMap((entry) => [
    { id: `${entry.id}-w`, name: `West ${entry.name}`, pos: { x: entry.x, y: entry.y } },
    { id: `${entry.id}-e`, name: `East ${entry.name}`, pos: { x: width - entry.x, y: entry.y } },
  ]);
}

function startingDeposits(cores: { x: number; y: number }[], width: number) {
  return cores.flatMap((core, index) => {
    const towardCenter = core.x < width / 2 ? 1 : -1;
    return [
      { type: 'power' as const, pos: { x: core.x - towardCenter * 9, y: core.y - 10 }, amount: 2000, gatherSlots: 4 }, // TEST
      { type: 'power' as const, pos: { x: core.x - towardCenter * 9, y: core.y + 10 }, amount: 2000, gatherSlots: 4 }, // TEST
      { type: 'essence' as const, pos: { x: core.x + towardCenter * 9, y: core.y + (index % 2 ? 9 : -9) }, amount: 600, gatherSlots: 4 }, // TEST
    ];
  });
}

function compactBaseRing(core: { x: number; y: number }, towardCenter: 1 | -1, group: string): RuinSpec[] {
  const frontX = core.x + towardCenter * 25;
  const rearX = core.x - towardCenter * 18;
  const ruins: RuinSpec[] = [];
  for (const dy of [-22, -11, 0, 11, 22]) {
    ruins.push({ pos: { x: frontX, y: core.y + dy }, cost: 13, gateSpan: dy === 0 ? 8 : 0, rot: 0, group }); // TEST
  }
  for (const dx of [rearX, core.x - towardCenter * 7, core.x + towardCenter * 5, core.x + towardCenter * 17]) {
    ruins.push({ pos: { x: dx, y: core.y - 27 }, cost: 13, gateSpan: 0, rot: 1, group }); // TEST
    ruins.push({ pos: { x: dx, y: core.y + 27 }, cost: 13, gateSpan: 0, rot: 1, group }); // TEST
  }
  return ruins;
}

const KINGS_FORK_CORES = [{ x: 26, y: 90 }, { x: 244, y: 90 }];
export const KINGS_FORK: MapDef = {
  id: 'kings-fork',
  name: "King's Fork",
  description: 'A forked ridge offers a fast exposed center push and two wider flanking marches.',
  mode: '1v1',
  width: 270, // TEST
  height: 180, // TEST
  corePositions: KINGS_FORK_CORES,
  spawnTeams: [0, 1],
  coreTerritoryRadius: 28,
  sites: [ // TEST — provisional authored geometry
    { id: 'fork-center', name: 'Crown Fork', pos: { x: 135, y: 90 } },
    ...mirroredSites(270, [
      { id: 'fork-north', name: 'North Spur', x: 88, y: 137 },
      { id: 'fork-south', name: 'South Spur', x: 88, y: 43 },
    ]),
  ],
  deposits: [ // TEST — provisional expansion balance
    ...startingDeposits(KINGS_FORK_CORES, 270),
    { type: 'power', pos: { x: 78, y: 90 }, amount: 2500, gatherSlots: 4 },
    { type: 'power', pos: { x: 192, y: 90 }, amount: 2500, gatherSlots: 4 },
    { type: 'essence', pos: { x: 135, y: 30 }, amount: 1200, gatherSlots: 4 },
    { type: 'essence', pos: { x: 135, y: 150 }, amount: 1200, gatherSlots: 4 },
    { type: 'power', pos: { x: 135, y: 102 }, amount: 3000, gatherSlots: 4 },
  ],
  ruins: [
    ...compactBaseRing(KINGS_FORK_CORES[0], 1, 'kings-w'),
    ...compactBaseRing(KINGS_FORK_CORES[1], -1, 'kings-e'),
    { pos: { x: 116, y: 90 }, gateSpan: 8 },
    { pos: { x: 154, y: 90 }, gateSpan: 8 },
  ],
  rocks: [ // TEST — provisional lane geometry
    { pos: { x: 116, y: 66 }, w: 8, h: 34 },
    { pos: { x: 116, y: 114 }, w: 8, h: 34 },
    { pos: { x: 154, y: 66 }, w: 8, h: 34 },
    { pos: { x: 154, y: 114 }, w: 8, h: 34 },
  ],
};

const HOLLOWMERE_CORES = [{ x: 25, y: 100 }, { x: 225, y: 100 }];
export const HOLLOWMERE_BASIN: MapDef = {
  id: 'hollowmere-basin',
  name: 'Hollowmere Basin',
  description: 'A broken stone basin encloses the rich center, with four defensible entrances around it.',
  mode: '1v1',
  width: 250, // TEST
  height: 200, // TEST
  corePositions: HOLLOWMERE_CORES,
  spawnTeams: [0, 1],
  coreTerritoryRadius: 28,
  sites: [ // TEST — provisional authored geometry
    { id: 'basin-center', name: 'Hollowmere', pos: { x: 125, y: 100 } },
    { id: 'basin-nw', name: 'Northwest Gate', pos: { x: 84, y: 150 } },
    { id: 'basin-ne', name: 'Northeast Gate', pos: { x: 166, y: 150 } },
    { id: 'basin-sw', name: 'Southwest Gate', pos: { x: 84, y: 50 } },
    { id: 'basin-se', name: 'Southeast Gate', pos: { x: 166, y: 50 } },
  ],
  deposits: [ // TEST — provisional expansion balance
    ...startingDeposits(HOLLOWMERE_CORES, 250),
    { type: 'power', pos: { x: 72, y: 100 }, amount: 2500, gatherSlots: 4 },
    { type: 'power', pos: { x: 178, y: 100 }, amount: 2500, gatherSlots: 4 },
    { type: 'essence', pos: { x: 125, y: 156 }, amount: 1200, gatherSlots: 4 },
    { type: 'essence', pos: { x: 125, y: 44 }, amount: 1200, gatherSlots: 4 },
    { type: 'power', pos: { x: 125, y: 112 }, amount: 3000, gatherSlots: 4 },
  ],
  ruins: [
    ...compactBaseRing(HOLLOWMERE_CORES[0], 1, 'hollow-w'),
    ...compactBaseRing(HOLLOWMERE_CORES[1], -1, 'hollow-e'),
  ],
  rocks: [ // TEST — provisional basin geometry
    { pos: { x: 96, y: 100 }, w: 24, h: 8 },
    { pos: { x: 154, y: 100 }, w: 24, h: 8 },
    { pos: { x: 125, y: 70 }, w: 8, h: 24 },
    { pos: { x: 125, y: 130 }, w: 8, h: 24 },
  ],
};

const CAUSEWAY_CORES = [{ x: 26, y: 85 }, { x: 254, y: 85 }];
export const SHATTERED_CAUSEWAY: MapDef = {
  id: 'shattered-causeway',
  name: 'Shattered Causeway',
  description: 'Three rubble-lined routes can be fortified into shifting front lines or broken open by siege.',
  mode: '1v1',
  width: 280, // TEST
  height: 170, // TEST
  corePositions: CAUSEWAY_CORES,
  spawnTeams: [0, 1],
  coreTerritoryRadius: 28,
  sites: [ // TEST — provisional authored geometry
    { id: 'cause-center', name: 'Broken Crown', pos: { x: 140, y: 85 } },
    { id: 'cause-nw', name: 'Northwest Span', pos: { x: 88, y: 128 } },
    { id: 'cause-ne', name: 'Northeast Span', pos: { x: 192, y: 128 } },
    { id: 'cause-sw', name: 'Southwest Span', pos: { x: 88, y: 42 } },
    { id: 'cause-se', name: 'Southeast Span', pos: { x: 192, y: 42 } },
  ],
  deposits: [ // TEST — provisional expansion balance
    ...startingDeposits(CAUSEWAY_CORES, 280),
    { type: 'power', pos: { x: 75, y: 85 }, amount: 2500, gatherSlots: 4 },
    { type: 'power', pos: { x: 205, y: 85 }, amount: 2500, gatherSlots: 4 },
    { type: 'essence', pos: { x: 140, y: 26 }, amount: 1200, gatherSlots: 4 },
    { type: 'essence', pos: { x: 140, y: 144 }, amount: 1200, gatherSlots: 4 },
    { type: 'power', pos: { x: 140, y: 98 }, amount: 3000, gatherSlots: 4 },
  ],
  ruins: [
    ...compactBaseRing(CAUSEWAY_CORES[0], 1, 'cause-w'),
    ...compactBaseRing(CAUSEWAY_CORES[1], -1, 'cause-e'),
    ...[54, 85, 116].flatMap((y) => [
      { pos: { x: 118, y }, gateSpan: 8 } as RuinSpec,
      { pos: { x: 162, y }, gateSpan: 8 } as RuinSpec,
    ]),
  ],
  rocks: [ // TEST — provisional route geometry
    { pos: { x: 118, y: 23 }, w: 7, h: 48 },
    { pos: { x: 118, y: 147 }, w: 7, h: 48 },
    { pos: { x: 162, y: 23 }, w: 7, h: 48 },
    { pos: { x: 162, y: 147 }, w: 7, h: 48 },
  ],
};

const FOURFOLD_CORES = [
  { x: 30, y: 72 }, { x: 30, y: 168 }, { x: 310, y: 72 }, { x: 310, y: 168 },
];
export const FOURFOLD_CROSSING: MapDef = {
  id: 'fourfold-crossing',
  name: 'Fourfold Crossing',
  description: 'Paired team bases converge on an open crossroads while nine sites reward coordinated lane control.',
  mode: '2v2',
  width: 340, // TEST
  height: 240, // TEST
  corePositions: FOURFOLD_CORES,
  spawnTeams: [0, 0, 1, 1],
  coreTerritoryRadius: 27,
  sites: [ // TEST — provisional authored geometry
    { id: 'four-center', name: 'Crossroads', pos: { x: 170, y: 120 } },
    ...mirroredSites(340, [
      { id: 'four-upper', name: 'Upper March', x: 112, y: 178 },
      { id: 'four-lower', name: 'Lower March', x: 112, y: 62 },
      { id: 'four-mid-n', name: 'North Ward', x: 92, y: 120 },
      { id: 'four-mid-s', name: 'South Ward', x: 132, y: 120 },
    ]),
  ],
  deposits: [ // TEST — provisional 2v2 balance
    ...startingDeposits(FOURFOLD_CORES, 340),
    ...[82, 120, 158].flatMap((y) => [
      { type: 'power' as const, pos: { x: 105, y }, amount: 2500, gatherSlots: 4 },
      { type: 'power' as const, pos: { x: 235, y }, amount: 2500, gatherSlots: 4 },
    ]),
    { type: 'essence', pos: { x: 170, y: 42 }, amount: 1600, gatherSlots: 4 },
    { type: 'essence', pos: { x: 170, y: 198 }, amount: 1600, gatherSlots: 4 },
    { type: 'power', pos: { x: 170, y: 132 }, amount: 3600, gatherSlots: 4 },
  ],
  ruins: FOURFOLD_CORES.flatMap((core, index) => compactBaseRing(core, index < 2 ? 1 : -1, `four-${index}`)),
  rocks: [ // TEST — provisional crossroads geometry
    { pos: { x: 145, y: 78 }, w: 9, h: 48 },
    { pos: { x: 145, y: 162 }, w: 9, h: 48 },
    { pos: { x: 195, y: 78 }, w: 9, h: 48 },
    { pos: { x: 195, y: 162 }, w: 9, h: 48 },
  ],
};

const DIADEM_CORES = [
  { x: 32, y: 38 }, { x: 328, y: 202 }, { x: 328, y: 38 }, { x: 32, y: 202 },
];
export const DIADEM_BASIN: MapDef = {
  id: 'diadem-basin',
  name: 'Diadem Basin',
  description: 'Diagonal allies must reinforce across the basin while each Core begins close to an enemy frontier.',
  mode: '2v2',
  width: 360, // TEST
  height: 240, // TEST
  corePositions: DIADEM_CORES,
  spawnTeams: [0, 0, 1, 1],
  coreTerritoryRadius: 27,
  sites: [ // TEST — provisional authored geometry
    { id: 'diadem-center', name: 'Diadem', pos: { x: 180, y: 120 } },
    { id: 'diadem-nw', name: 'Northwest Jewel', pos: { x: 95, y: 68 } },
    { id: 'diadem-ne', name: 'Northeast Jewel', pos: { x: 265, y: 68 } },
    { id: 'diadem-sw', name: 'Southwest Jewel', pos: { x: 95, y: 172 } },
    { id: 'diadem-se', name: 'Southeast Jewel', pos: { x: 265, y: 172 } },
    { id: 'diadem-n', name: 'North Point', pos: { x: 180, y: 42 } },
    { id: 'diadem-s', name: 'South Point', pos: { x: 180, y: 198 } },
    { id: 'diadem-w', name: 'West Point', pos: { x: 78, y: 120 } },
    { id: 'diadem-e', name: 'East Point', pos: { x: 282, y: 120 } },
  ],
  deposits: [ // TEST — provisional 2v2 balance
    ...startingDeposits(DIADEM_CORES, 360),
    ...[
      { x: 112, y: 120 }, { x: 248, y: 120 }, { x: 180, y: 66 }, { x: 180, y: 174 },
    ].map((pos) => ({ type: 'power' as const, pos, amount: 2700, gatherSlots: 4 })),
    { type: 'essence', pos: { x: 165, y: 120 }, amount: 1800, gatherSlots: 4 },
    { type: 'essence', pos: { x: 195, y: 120 }, amount: 1800, gatherSlots: 4 },
  ],
  ruins: DIADEM_CORES.flatMap((core, index) => compactBaseRing(core, core.x < 180 ? 1 : -1, `diadem-${index}`)),
  rocks: [ // TEST — provisional basin geometry
    { pos: { x: 145, y: 92 }, w: 30, h: 8 },
    { pos: { x: 215, y: 92 }, w: 30, h: 8 },
    { pos: { x: 145, y: 148 }, w: 30, h: 8 },
    { pos: { x: 215, y: 148 }, w: 30, h: 8 },
  ],
};

export const MAPS: Record<MapId, MapDef> = {
  'crownline-crossing': CROWNLINE_CROSSING,
  'kings-fork': KINGS_FORK,
  'hollowmere-basin': HOLLOWMERE_BASIN,
  'shattered-causeway': SHATTERED_CAUSEWAY,
  'fourfold-crossing': FOURFOLD_CROSSING,
  'diadem-basin': DIADEM_BASIN,
};

export function mapDef(id: MapId): MapDef {
  return MAPS[id];
}

export function mapsForMode(mode: MatchMode): MapDef[] {
  return Object.values(MAPS).filter((map) => map.mode === mode);
}

export function playerIdsForMode(mode: MatchMode): PlayerId[] {
  return (mode === '2v2' ? [0, 1, 2, 3] : [0, 1]) as PlayerId[];
}
